import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useCustomToast,
  useDisclosure,
} from '@elkaso-app/web-design';
import { useUpdateInvoicePaymentApi } from 'apis/reconciliation';
import { useState } from 'react';

export interface IMarkPaidPopover {
  invoiceId: number;
  /** Called after a successful mark-paid — the parent owns query invalidation. */
  onSaved: () => void;
}

/** Single click → confirmation popover with an optional free-text payment note (the payer may not
 * be a system user), then the chip flips via the parent's invalidation. The success toast calls out
 * the collected-amount ripple because it moves the board status. */
export const MarkPaidPopover = ({ invoiceId, onSaved }: IMarkPaidPopover) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useCustomToast();
  const [note, setNote] = useState('');

  const { mutate: updatePayment, isLoading } = useUpdateInvoicePaymentApi({
    onSuccess: () => {
      toast({ description: 'Marked as paid — the month’s collected amount has been updated', status: 'success' });
      onClose();
      onSaved();
    },
  });

  const openFresh = () => {
    setNote('');
    onOpen();
  };

  const submit = () => updatePayment({ invoiceId, isPaid: true, paidNote: note.trim() || undefined });

  return (
    <Popover isOpen={isOpen} onClose={onClose} placement='bottom-end' isLazy>
      <PopoverTrigger>
        <Button size='xs' variant='outline' colorScheme='green' onClick={openFresh}>
          Mark paid
        </Button>
      </PopoverTrigger>
      <PopoverContent w='280px' zIndex='popover'>
        <PopoverArrow />
        <PopoverBody p='3'>
          <Text fontSize='sm' fontWeight='semibold' mb='2'>
            Mark this invoice as paid?
          </Text>
          <FormControl mb='3'>
            <FormLabel fontSize='xs' mb='1'>
              Payment note (optional)
            </FormLabel>
            <Input
              size='sm'
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder='e.g. cash to driver, transfer ref…'
              autoFocus
            />
          </FormControl>
          <Flex gap='2' justify='flex-end'>
            <Button size='xs' variant='outline' onClick={onClose} isDisabled={isLoading}>
              Cancel
            </Button>
            <Button size='xs' colorScheme='green' onClick={submit} isLoading={isLoading}>
              Mark paid
            </Button>
          </Flex>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
